import React from "react";
import { MenuCategory, PageContent } from "../../../Core/Content/Types";

type SelectionKey = "featuredItemIds" | "featuredDrinkIds";

function FeaturedPicker({
  title,
  kind,
  categories,
  selected,
  onChange,
}: {
  title: string;
  kind: "food" | "drink";
  categories: MenuCategory[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("suggested");
  const [selectedOnly, setSelectedOnly] = React.useState(false);
  const items = categories.flatMap((group) =>
    group.items.map((item) => ({ ...item, category: group }))
  );
  const isDrink = (group: MenuCategory) =>
    group.id === "sipsAndSwigs" ||
    /drink|beverage|cocktail|sips|swigs/i.test(group.title);
  const results = items.filter((item) => {
    const categoryMatches =
      category === "all" ||
      (category === "suggested"
        ? kind === "drink"
          ? isDrink(item.category)
          : !isDrink(item.category)
        : item.category.id === category);
    return (
      categoryMatches &&
      (!selectedOnly || selected.includes(item.id)) &&
      `${item.title} ${item.category.title}`
        .toLowerCase()
        .includes(query.trim().toLowerCase())
    );
  });
  const toggle = (id: string) =>
    onChange(
      selected.includes(id)
        ? selected.filter((value) => value !== id)
        : [...selected, id]
    );
  return (
    <section className="cms-featured-picker" aria-label={title}>
      <div className="cms-featured-heading">
        <h3>{title}</h3>
        <span>{selected.length} selected</span>
      </div>
      {selected.length ? (
        <ul className="cms-featured-selected" aria-label={`Selected ${kind}s`}>
          {selected.map((id) => {
            const item = items.find((item) => item.id === id);
            return (
              <li key={id}>
                <span>
                  {item?.title || "Item no longer in menu"}
                  {item && (!item.visible || !item.category.visible) && (
                    <small>Hidden in menu draft</small>
                  )}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${
                    item?.title || "missing item"
                  } from ${title.toLowerCase()}`}
                  onClick={() => toggle(id)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="cms-featured-empty">
          No {kind}s selected yet. Choose a few favorites below.
        </p>
      )}
      <details>
        <summary>Choose {kind}s</summary>
        <div className="cms-featured-filters">
          <label className="cms-field">
            <span>Search {kind}s</span>
            <input
              type="search"
              placeholder="Search by name…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className="cms-field">
            <span>Category</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="suggested">
                {kind === "drink" ? "Drink categories" : "Food categories"}
              </option>
              <option value="all">All menu categories</option>
              {categories.map((group) => (
                <option value={group.id} key={group.id}>
                  {group.title}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="cms-toggle">
          <input
            type="checkbox"
            checked={selectedOnly}
            onChange={(event) => setSelectedOnly(event.target.checked)}
          />
          Show selected only
        </label>
        <div
          className="cms-featured-results"
          role="group"
          aria-label={`Available ${kind}s`}
          tabIndex={0}
        >
          {results.map((item) => (
            <label className="cms-featured-option" key={item.id}>
              <input
                type="checkbox"
                checked={selected.includes(item.id)}
                onChange={() => toggle(item.id)}
              />
              <span>
                <strong>{item.title}</strong>
                <small>
                  {item.category.title}
                  {(!item.visible || !item.category.visible) &&
                    " · Hidden in menu draft"}
                </small>
              </span>
              <span className="cms-featured-price">{item.price}</span>
            </label>
          ))}
          {!results.length && (
            <p>No matches. Try another search or choose all menu categories.</p>
          )}
        </div>
        <small>
          {results.length} matching items · Check to add, uncheck to remove.
        </small>
      </details>
    </section>
  );
}

export default function FeaturedMenu({
  content,
  menu,
  onChange,
}: {
  content: PageContent;
  menu: PageContent | null;
  onChange: (content: PageContent) => void;
}) {
  return (
    <section className="cms-card cms-featured-menu">
      <h2>Home page favorites</h2>
      <p>
        Choose the foods and drinks you want to highlight on Home. You only need
        to pick them here—update their prices and availability in Menu.
      </p>
      <label className="cms-toggle">
        <input
          type="checkbox"
          checked={content.useMenuReferences}
          onChange={(event) =>
            onChange({ ...content, useMenuReferences: event.target.checked })
          }
        />
        Use my menu selections on Home
      </label>
      <p className="cms-note">
        {content.useMenuReferences
          ? "After you publish Home, these selections replace the current featured foods and drinks. Later menu changes carry over when you publish Menu."
          : "Your current Home favorites will stay as they are. Choose your items below, then turn this on when you’re ready to use them."}
      </p>
      {menu ? (
        <div className="cms-featured-grid">
          {(["featuredItemIds", "featuredDrinkIds"] as SelectionKey[]).map(
            (key) => (
              <FeaturedPicker
                key={key}
                title={
                  key === "featuredItemIds"
                    ? "Featured foods"
                    : "Featured drinks"
                }
                kind={key === "featuredItemIds" ? "food" : "drink"}
                categories={menu.categories}
                selected={content[key]}
                onChange={(ids) => onChange({ ...content, [key]: ids })}
              />
            )
          )}
        </div>
      ) : (
        <p role="status">
          Menu items couldn’t be loaded. Open Menu, then return here to try
          again.
        </p>
      )}
      {content.useMenuReferences &&
        (!content.featuredItemIds.length ||
          !content.featuredDrinkIds.length) && (
          <p className="cms-note">
            A section with no selections will have no featured items after
            publishing.
          </p>
        )}
      <details className="cms-featured-help">
        <summary>How do these selections go live?</summary>
        <ol>
          <li>
            Add or update items in <a href="/dashboard/pages/menu">Menu</a>,
            then publish Menu first.
          </li>
          <li>
            Choose your Home favorites and turn on “Use my menu selections on
            Home.”
          </li>
          <li>Save this draft, preview it, then publish Home.</li>
        </ol>
        <p>
          These choices use your menu draft. The public Home page uses the
          published menu. Items hidden in the published menu won’t appear on
          Home.
        </p>
      </details>
    </section>
  );
}
